import { useMemo } from 'react';
import { NavLink, useNavigate } from 'react-router';
import { useAuth } from '@/contexts/AuthContext';
import { useGroups } from '@/hooks/useGroups';
import { sortGroupsForUser } from '@/lib/sortGroups';
import {
  LayoutDashboard,
  Receipt,
  Activity,
  BarChart3,
  Wallet,
  LogOut,
  X,
} from 'lucide-react';
import { ShareCostLogo } from '@/components/common/ShareCostLogo';
import { UserAvatar } from '@/components/common/UserAvatar';
import { ThemeToggle } from '@/components/common/ThemeToggle';
import { usePendingTransactionCount } from '@/hooks/useWallet';
import { useTranslation } from 'react-i18next';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

// Translated at render time (module-scope constants run before the bundles load)
const navItems = [
  { to: '/dashboard', labelKey: 'web:sidebar.dashboard', icon: LayoutDashboard },
  { to: '/expenses', labelKey: 'web:sidebar.expenses', icon: Receipt },
  { to: '/activities', labelKey: 'web:sidebar.activity', icon: Activity },
  { to: '/statistics', labelKey: 'web:sidebar.statistics', icon: BarChart3 },
  { to: '/wallet', labelKey: 'web:common.walletImport', icon: Wallet, showBadge: true as const, badgeType: 'wallet' as const },
];

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { t } = useTranslation();
  const { user, profile, signOut } = useAuth();
  const navigate = useNavigate();
  const { data: groups } = useGroups();
  const sortedGroups = useMemo(
    () => (groups && user ? sortGroupsForUser(groups, user.id) : groups),
    [groups, user]
  );
  const { data: walletPendingCount = 0 } = usePendingTransactionCount();

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-50 flex w-64 flex-col bg-surface-primary border-r border-border-primary transition-transform duration-200
          lg:static lg:translate-x-0
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Logo area */}
        <div className="flex h-16 items-center justify-between px-6 border-b border-border-tertiary">
          <NavLink to="/dashboard" className="flex items-center gap-2" onClick={onClose}>
            <ShareCostLogo className="h-7 w-7" />
            <span className="text-xl font-bold text-text-primary">{t('auth:title')}</span>
          </NavLink>
          <button
            onClick={onClose}
            className="rounded-md p-1 text-text-quaternary hover:text-text-secondary lg:hidden"
          >
            <X className="h-5 w-5" />
          </button>
        </div>


        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto space-y-1 px-3">
          {navItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={onClose}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300'
                    : 'text-text-secondary hover:bg-surface-hover hover:text-text-primary'
                }`
              }
            >
              <item.icon className="h-5 w-5" />
              <span className="flex-1">{t(item.labelKey)}</span>
              {item.showBadge && item.badgeType === 'wallet' && walletPendingCount > 0 && (
                <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-500 px-1.5 text-xs font-medium text-white">
                  {walletPendingCount > 99 ? '99+' : walletPendingCount}
                </span>
              )}
            </NavLink>
          ))}

          {/* Groups section */}
          <div className="pt-4 pb-1 px-3">
            <span className="text-xs font-semibold uppercase tracking-wider text-text-quaternary">
              {t('common:tab.groups')}
            </span>
          </div>

          {sortedGroups && sortedGroups.length > 0 ? (
            sortedGroups.map(group => (
              <NavLink
                key={group.id}
                to={`/groups/${group.id}`}
                onClick={onClose}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300'
                      : 'text-text-secondary hover:bg-surface-hover hover:text-text-primary'
                  }`
                }
              >
                <div
                  className="h-3 w-3 rounded-full shrink-0"
                  style={{ backgroundColor: group.color ?? '#6366F1' }}
                />
                <span className="truncate flex-1">{group.name}</span>
              </NavLink>
            ))
          ) : sortedGroups ? (
            <div className="px-3 py-2 text-sm text-text-quaternary">
              {t('groups:empty.noGroups')}
            </div>
          ) : (
            <div className="px-3 py-2 text-sm text-text-quaternary italic">
              {t('common:loading')}
            </div>
          )}
        </nav>

        {/* Theme toggle */}
        <div className="border-t border-border-primary px-4 py-3 flex justify-center">
          <ThemeToggle />
        </div>

        {/* User section */}
        <div className="border-t border-border-primary p-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/profile')} className="shrink-0">
              <UserAvatar name={profile?.display_name} avatarUrl={profile?.avatar_url} size="lg" />
            </button>
            <button onClick={() => navigate('/profile')} className="flex-1 min-w-0 text-left">
              <p className="truncate text-sm font-medium text-text-primary">
                {profile?.display_name ?? t('web:common.user')}
              </p>
              <p className="truncate text-xs text-text-tertiary">
                {profile?.email}
              </p>
            </button>
            <button
              onClick={handleSignOut}
              className="rounded-md p-1.5 text-text-quaternary hover:bg-surface-hover hover:text-text-secondary"
              title={t('common:button.signOut')}
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}
